import { getSortedTimePeriod } from 'utils/TimeFormatUtil';
import { apiWithResponseHandle } from './LoadingUtil';
import { getAdminAlertList, getNewestAlertTime, updateAdminAlert, updateAllAdminAlerts } from 'services/AdminAlertAPIHelper';
import { ALERT_STATUS } from 'config/CustomEnums';

const getInitialState = () => ({
  isLoadingList: false,
  totalUnread: 0,
  allAlerts: [],
  newestAlertTime: undefined,
  pageInfo: {
    startCursor: "",
    endCursor: "",
    hasPreviousPage: false,
    hasNextPage: false,
  },
});

const parseAlert = (alertData) => {
  return {
    ...alertData
  }
}

const getExistingAlertListTimePeriod = (existingAlertList, timeFieldName ="creationDate") => {
  // existingAlertList should sorted by creation date
  if (!existingAlertList?.length > 0) {
    return ["", ""]
  }
  if (existingAlertList?.length === 1) {
    return [existingAlertList[0]?.[timeFieldName] || "", existingAlertList[0]?.[timeFieldName] || ""]
  }
  const firstTime = existingAlertList[0]?.[timeFieldName] || "";
  const lastTime = existingAlertList[existingAlertList.length - 1]?.[timeFieldName] || "";
  return getSortedTimePeriod(firstTime, lastTime)
}

export default {
  namespace: 'alertCentre',
  state: { ...getInitialState() },
  reducers: {
    updateState(state, { payload }) {
      return {
        ...state,
        ...payload,
      };
    },
    updateOneAlert(state, { payload }) {
      const { updateBody } = payload
      const { id: alertPk, ...updateValues } = updateBody
      let newTotalUnread = state.totalUnread
      const newAlertList = state.allAlerts?.map(alertInfo=>{
        if (alertInfo.pk === alertPk) {
          if (updateValues?.status === ALERT_STATUS.COMPLETED) {
            newTotalUnread -= 1
          } else if (updateValues?.status === ALERT_STATUS.INITIAL) {
            newTotalUnread += 1
          }
          return {
            ...alertInfo,
            ...updateValues
          }
        }
        return alertInfo
      })
      return {
        ...state,
        totalUnread: newTotalUnread,
        allAlerts: newAlertList,
      }
    },
    updateAllAlerts(state, { payload }) {
      const { updateBody } = payload
      const newAlertList = state.allAlerts?.map(
        alertInfo => ({
          ...alertInfo,
          ...updateBody
        })
      )
      return {
        ...state,
        allAlerts: newAlertList,
        totalUnread: updateBody?.status === ALERT_STATUS.COMPLETED ? 0 : state.totalUnread
      }
    },
    clearData(state, { payload }) {
      return { ...state, ...getInitialState() };
    },
    assembleAlertList(state, { payload }) {
      const {
        totalUnread,
        pageInfo,
        alertList,
        newestAlertTime,
        isLoadMore,
      } = payload
      const parsedAlertList = alertList?.map(item => parseAlert(item?.node))

      return {
        ...state,
        totalUnread,
        pageInfo,
        newestAlertTime,
        allAlerts: isLoadMore
          ? [...state.allAlerts, ...parsedAlertList]
          : parsedAlertList
      };
    },
  },
  effects: {
    getNewestAlertTime: [
      function* ({ payload }, { put, call, select }) {
        const serviceArgs = [
          getNewestAlertTime,
        ];

        function* onSuccess(data) {
          const adminAlerts = data?.adminAlerts;
          yield put({
            type: 'updateState',
            payload: {
              newestAlertTime: adminAlerts?.newestAlertTime,
              totalUnread: adminAlerts?.totalToDo,
            },
          });
        }
        function* onFailed(data) {
        }
        yield apiWithResponseHandle(serviceArgs, onSuccess, onFailed, onFailed);
      }
    ],
    getPagedAlertList: [
      function* ({ payload }, { put, call, select }) {
        const {
          isLoadMore,
          search,
          statusIn,
          afterAction,
          creationDateGte,
          creationDateLte,
          orderBy,
        } = payload;
        const afterCursor = '';
        let creationDateTimeGt = '';
        let creationDateTimeLt = '';
        if (isLoadMore) {
          const existingAllAlerts = yield select(
            (state) => state.alertCentre.allAlerts,
          );
          const [startTime, endTime] = getExistingAlertListTimePeriod(existingAllAlerts)
          if (!orderBy || orderBy === "-creationDate") {
            creationDateTimeLt = startTime
          }
          if (orderBy === "creationDate") {
            creationDateTimeGt = endTime
          }
        } else {
          yield put({
            type: 'updateState',
            payload: {
              isLoadingList: true,
            },
          });
        }
        const serviceArgs = [
          getAdminAlertList,
          afterCursor,
          search,
          statusIn,
          creationDateGte,
          creationDateLte,
          creationDateTimeGt,
          creationDateTimeLt,
          orderBy
        ];

        function* onSuccess(data) {
          const adminAlerts = data?.adminAlerts;
          yield put({
            type: 'assembleAlertList',
            payload: {
              totalUnread: adminAlerts?.totalToDo,
              pageInfo: adminAlerts?.pageInfo,
              alertList: adminAlerts?.edges,
              newestAlertTime: adminAlerts?.newestAlertTime,
              isLoadMore,
            },
          });
          if (!isLoadMore) {
            yield put({
              type: 'updateState',
              payload: {
                isLoadingList: false,
              },
            });
          }
          afterAction && afterAction()
        }
        function* onFailed(data) {
          if (!isLoadMore) {
            yield put({
              type: 'updateState',
              payload: {
                isLoadingList: false,
              },
            });
          }
          afterAction && afterAction()
        }
        yield apiWithResponseHandle(serviceArgs, onSuccess, onFailed, onFailed);
      },
    ],
    updateAdminAlert: [
      function* ({ payload }, { put, call }) {
        const {actionAfterSuccess, ...updateBody} = payload
        const { id: alertPk } = updateBody
        const serviceArgs = [updateAdminAlert, updateBody];

        function* onSuccess(data) {
          if (alertPk) {
            yield put({
              type: 'updateOneAlert',
              payload: {
                updateBody
              },
            });
          }
          actionAfterSuccess && actionAfterSuccess()
        }
        yield apiWithResponseHandle(serviceArgs, onSuccess);
      },
    ],
    updateAllAdminAlerts: [
      function* ({ payload }, { put, call }) {
        const { actionAfterSuccess, ...updateBody } = payload
        const serviceArgs = [updateAllAdminAlerts, updateBody];

        function* onSuccess(data) {
          yield put({
            type: 'updateAllAlerts',
            payload: { updateBody },
          });
          actionAfterSuccess && actionAfterSuccess()
        }
        yield apiWithResponseHandle(serviceArgs, onSuccess);
      },
    ]
  },
};
